// src/pages/Insurance.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';
import { deleteInsuranceRecord, fetchInsuranceData, saveOrUpdateInsuranceRecord } from '../services/insuranceService';
import { fetchDependents } from '../services/assetService';
import { LookupValue } from '../services/lookupService';
import { RefreshCw } from 'lucide-react';

// Import Child Components
import Notification from '../components/shared/Notification';
import InsuranceHeader from '../components/insurance/InsuranceHeader';
import InsuranceForm from '../components/insurance/InsuranceForm';
import InsurancesTable from '../components/insurance/InsurancesTable';
import InsurancesMobileView from '../components/insurance/InsurancesMobileView';
import ActionBar from '../components/insurance/ActionBar';
import EmptyState from '../components/insurance/EmptyState';
import Pagination from '../components/shared/Pagination';

export interface InsuranceData {
  id: string;
  insuranceOwnerType: string;
  insuranceType: string;
  insuranceSubType: string;
  insuranceName: string;
  insuranceStartDate: string;
  insuranceEndDate: string;
  totalInsuranceAmount: string;
  insuranceInstallmentAmount: string;
  insuranceInstallmentFrequency: string;
  totalFamilyInsurance: string;
  totalFamilyInsuranceInstallmentAmount: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof InsuranceData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel' | 'frequencyLabel';

const visibleColumns: ColumnKey[] = [
  'insuranceOwnerType', 'insuranceName', 'insuranceType', 'insuranceSubType',
  'totalInsuranceAmount', 'insuranceInstallmentAmount', 'insuranceInstallmentFrequency', 'insuranceEndDate',
];

const getColumnDisplayName = (col: ColumnKey): string => {
    const displayNames: Record<ColumnKey, string> = {
    id: 'ID', insuranceOwnerType: 'Owner Type', insuranceType: 'Type', insuranceSubType: 'Sub Type',
    insuranceName: 'Policy Name', insuranceStartDate: 'Start Date', insuranceEndDate: 'End Date',
    totalInsuranceAmount: 'Coverage Amount', insuranceInstallmentAmount: 'Installment',
    insuranceInstallmentFrequency: 'Frequency', totalFamilyInsurance: 'Total Family Coverage',
    totalFamilyInsuranceInstallmentAmount: 'Total Family Installment', status: 'Status',
    familyMemberId: '', ownerTypeLabel: 'Owner Type', typeLabel: 'Type',
    subTypeLabel: 'Sub Type', frequencyLabel: 'Frequency',
  };
  return displayNames[col];
};

const Insurance: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();
    
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [transformedData, setTransformedData] = useState<InsuranceData[]>([]);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<InsuranceData>({
      id: '', insuranceOwnerType: '', insuranceType: '', insuranceSubType: '', insuranceName: '', insuranceStartDate: '', insuranceEndDate: '',
      totalInsuranceAmount: '', insuranceInstallmentAmount: '', insuranceInstallmentFrequency: '', totalFamilyInsurance: '',
      totalFamilyInsuranceInstallmentAmount: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{ ownerType: LookupValue[]; insuranceType: LookupValue[]; insuranceSubType: LookupValue[]; insuranceInstallmentFrequency: LookupValue[]; }>({
      ownerType: [], insuranceType: [], insuranceSubType: [], insuranceInstallmentFrequency: [],
    });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null; }>({ key: 'insuranceName', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    
    const lookupGroupMap: Record<string, string> = {
      insuranceOwnerType: 'ownerType', insuranceType: 'insuranceType',
      insuranceSubType: 'insuranceSubType', insuranceInstallmentFrequency: 'insuranceInstallmentFrequency',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(num))}`;
    }, [state.currencySymbol]);

    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        const adjustedDate = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
        return new Intl.DateTimeFormat('en-GB').format(adjustedDate);
      } catch { return dateString; }
    };
    
    const resetForm = () => {
        setFormData({
          id: '', insuranceOwnerType: '', insuranceType: '', insuranceSubType: '', insuranceName: '', insuranceStartDate: '', insuranceEndDate: '',
          totalInsuranceAmount: '', insuranceInstallmentAmount: '', insuranceInstallmentFrequency: '', totalFamilyInsurance: '',
          totalFamilyInsuranceInstallmentAmount: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchInsuranceFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchInsuranceData();
            setTransformedData(apiData.data as InsuranceData[]);
        } catch (error) {
            console.error('Failed to fetch insurance data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch insurance.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);

    useEffect(() => { fetchInsuranceFromAPI(); }, [fetchInsuranceFromAPI]);
    useEffect(() => { fetchDependents().then(setDependents); }, []);

    useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('ownerType'),
                insuranceType: getOptions('insuranceType'),
                insuranceInstallmentFrequency: getOptions('insuranceInstallmentFrequency'),
                insuranceSubType: [],
            });
        }
    }, [isLookupsLoading, getOptions]);

    useEffect(() => {
        const typeVal = formData.insuranceType;
        if (!typeVal) {
            setDropdownOptions(prev => ({ ...prev, insuranceSubType: [] }));
            return;
        }
        setLoadingSubTypes(true);
        const allSubTypes = getOptions('insuranceSubType');
        const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
        setDropdownOptions(prev => ({ ...prev, insuranceSubType: filteredSubs }));
        setLoadingSubTypes(false);
    }, [formData.insuranceType, getOptions]);

    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'insuranceOwnerType' && { familyMemberId: '' }) }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.insuranceOwnerType || !formData.insuranceName || !formData.insuranceType || !formData.insuranceInstallmentAmount) {
            return alert('Please fill in all required fields marked with *');
        }
        if (formData.insuranceOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateInsuranceRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchInsuranceFromAPI();
            setOperationStatus({ show: true, message: `Insurance ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save insurance.', type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };
    
    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: string | number | Date = group ? getLabel(group, a[key as keyof InsuranceData] as string) : a[key as keyof InsuranceData] ?? '';
            let bValue: string | number | Date = group ? getLabel(group, b[key as keyof InsuranceData] as string) : b[key as keyof InsuranceData] ?? '';

            if (['totalInsuranceAmount', 'insuranceInstallmentAmount'].includes(key)) {
                aValue = parseFloat(aValue as string || '0');
                bValue = parseFloat(bValue as string || '0');
            } else if (['insuranceStartDate', 'insuranceEndDate'].includes(key)) {
                aValue = new Date(aValue as string);
                bValue = new Date(bValue as string);
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };
    
    const handleEdit = (insurance: InsuranceData) => {
        setFormData(insurance);
        setEditingId(insurance.id);
        setShowForm(true);
    };

    // ✅ CORRECTED LOGIC: Update state directly for instant UI feedback.
    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this insurance record?')) {
            try {
                await deleteInsuranceRecord(id); // API call
                
                // Update local state directly instead of re-fetching the whole list
                setTransformedData(prevData => prevData.filter(item => item.id !== id));
                setSelectedRows(prev => prev.filter(rowId => rowId !== id));

                setOperationStatus({ show: true, message: 'Insurance record deleted successfully!', type: 'success' });
            } catch (error: any) {
                console.error('Delete failed:', error);
                setOperationStatus({ show: true, message: error.message || 'Failed to delete insurance record.', type: 'error' });
                // Optional: Re-fetch on error to sync state
                fetchInsuranceFromAPI();
            }
        }
    };
    
    // ✅ CORRECTED LOGIC: Update state directly for instant UI feedback.
    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected records?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteInsuranceRecord(id)));

                // Update local state directly
                setTransformedData(prevData => prevData.filter(item => !selectedRows.includes(item.id)));
                setSelectedRows([]);

                setOperationStatus({ show: true, message: 'Selected records deleted successfully!', type: 'success' });
            } catch (error: any) {
                console.error('Bulk delete failed:', error);
                setOperationStatus({ show: true, message: error.message || 'Failed to delete selected records.', type: 'error' });
                fetchInsuranceFromAPI();
            }
        }
    };

    const handleRowSelect = (id: string) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    const handleSelectAll = () => setSelectedRows(selectedRows.length === paginatedData.length ? [] : paginatedData.map(d => d.id));
    
    

    const filteredData = transformedData.filter(insurance => {
        const getDisplayValue = (colKey: keyof InsuranceData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, insurance[colKey] as string) : insurance[colKey] || '';
        };
        const searchMatch = searchTerm ? Object.keys(insurance).some(key => String(getDisplayValue(key as keyof InsuranceData)).toLowerCase().includes(searchTerm.toLowerCase())) : true;
        const filterMatch = Object.entries(columnFilters).every(([key, value]) => !value || String(getDisplayValue(key as keyof InsuranceData)).toLowerCase().includes(String(value).toLowerCase()));
        return searchMatch && filterMatch;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalInsurances = transformedData.reduce((sum, item) => sum + parseFloat(item.totalInsuranceAmount || '0'), 0);

    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); },
    };

    const getSortIndicator = (key: ColumnKey) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '');
    const handleAddClick = () => { resetForm(); setShowForm(true); };
    
    const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select...') => {
        if (isLookupsLoading) return <option>Loading...</option>;
        if (!options || options.length === 0) return <option>No options available</option>;
        return (<><option value="">{placeholder}</option>{options.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}</>);
    };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />
            
            <InsuranceHeader
                totalInsurances={totalInsurances}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <InsuranceForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={() => { setShowForm(false); resetForm(); }}
                    renderDropdownOptions={renderDropdownOptions}
                />
            )}
            
            <InsurancesMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                paginationProps={paginationProps}
            />
               {filteredData.length >0 && (
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                <ActionBar
                    searchTerm={searchTerm}
                    onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    onBulkDelete={handleBulkDelete}
                    selectedRowCount={selectedRows.length}
                />
               {apiLoading ? (
                      <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                  ) : (
                    <InsurancesTable
                        paginatedData={paginatedData}
                        visibleColumns={visibleColumns}
                        getColumnDisplayName={getColumnDisplayName}
                        getSortIndicator={getSortIndicator}
                        handleSort={handleSort}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        selectedRows={selectedRows}
                        handleSelectAll={handleSelectAll}
                        handleRowSelect={handleRowSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        formatCurrency={formatCurrency}
                        formatDate={formatDate}
                        getLabel={getLabel}
                        lookupGroupMap={lookupGroupMap}
                    />
                )}
                <Pagination {...paginationProps} />
            </div>
               )}

            {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Insurance;